import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { NEVER, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AuthService } from '../../features/auth/services/auth.service';
import { TERMS_DECLINED_NOTICE } from '../../features/auth/services/terms-gate.service';

const PASSWORD_EXPIRED_NOTICE =
  'Your password has expired. Sign in to get a link to set a new one.';

/** What the sign-in page says for each of sso-be's "not signed in" answers. */
const NOTICES: Record<string, string> = {
  TERMS_REQUIRED: TERMS_DECLINED_NOTICE,
  PASSWORD_EXPIRED: PASSWORD_EXPIRED_NOTICE,
};

/**
 * Turns sso-be's "not signed in" answers into a sign-out.
 *
 * A session whose user has not accepted the T&C, or whose password has
 * expired, is not signed in — sso-be refuses it everywhere except accepting
 * the Terms and signing out. Wherever that refusal arrives (a page opened by
 * URL, the picker, the security page), the session is ended and the browser
 * goes back to the sign-in page with the reason.
 */
@Injectable()
export class TermsRequiredInterceptor implements HttpInterceptor {
  constructor(
    private injector: Injector,
    private router: Router,
  ) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err) => {
        const notice = err instanceof HttpErrorResponse ? NOTICES[err.error?.status] : undefined;
        if (!notice) {
          return throwError(() => err);
        }
        const toLogin = () => this.router.navigate(['/login'], { state: { notice } });
        // Resolved here rather than injected: AuthService needs HttpClient, which needs this interceptor.
        this.injector.get(AuthService).logout().subscribe({ next: toLogin, error: toLogin });
        // The page is leaving; never settle, so it does not render its own error first.
        return NEVER;
      }),
    );
  }
}
